<script setup>
import { RouterLink } from "vue-router";
import GlIcon from "~/components/gitlab/GlIcon.vue";
import GlPopover from "~/components/gitlab/GlPopover.vue";
import GlToggle from "~/components/gitlab/GlToggle.vue";
import { iconHref } from "~/utils/icons.js";

const STATUS_ICONS = {
  failed: { color: "var(--gl-status-danger-icon-color)", name: "status-failed" },
  never: { color: "var(--gl-status-neutral-icon-color)", name: "status-neutral" },
  running: { color: "var(--gl-status-info-icon-color)", name: "status-running" },
  success: { color: "var(--gl-status-success-icon-color)", name: "status-success" },
  warning: { color: "var(--gl-status-warning-icon-color)", name: "status-alert" }
};

function statusIcon(status) {
  return STATUS_ICONS[status] ?? STATUS_ICONS.never;
}

defineProps({
  isAdmin: { default: false, type: Boolean },
  project: { required: true, type: Object }
});

const emit = defineEmits(["delete-request", "sync-request", "toggle"]);
</script>

<template>
  <li
    class="flex items-center gap-4 border-b border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] py-4"
    data-testid="project-entry"
    :data-project-name="project.name"
  >
    <!-- Project info -->
    <div :class="['min-w-0 flex-1', !project.enabled && 'opacity-50']">
      <h2 class="text-sm font-semibold leading-snug">
        <RouterLink
          :to="{ name: 'project', params: { projectId: project.id } }"
          data-testid="project-url"
          class="text-[var(--gl-text-color-link)] hover:underline"
          >{{ project.name }}</RouterLink
        >
      </h2>
      <a
        v-if="project.webUrl"
        :href="project.webUrl"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="gitlab-url"
        class="text-xs text-[var(--gl-text-color-subtle)] hover:text-[var(--gl-text-color-link)] hover:underline"
        >{{ project.webUrl }}</a
      >
    </div>

    <!-- Right side: status + meta + admin actions -->
    <div class="flex shrink-0 items-center gap-4">
      <div :class="['flex items-center gap-4', !project.enabled && 'opacity-50']">
        <!-- Last run status + time -->
        <span
          class="flex items-center gap-1.5 text-xs text-[var(--gl-text-color-subtle)]"
          :title="project.lastUpdateRunAt ?? 'Never run'"
        >
          <GlIcon
            :name="statusIcon(project.lastRunStatus ?? 'never').name"
            :size="16"
            :style="{ color: statusIcon(project.lastRunStatus ?? 'never').color }"
          />
          <span v-if="project.lastUpdateRunAt">{{ project.lastUpdateRunAt }}</span>
          <span v-else class="italic text-[var(--gl-text-color-disabled)]">never</span>
        </span>

        <!-- Job count -->
        <span class="flex items-center gap-1 text-xs text-[var(--gl-text-color-subtle)]" title="Configured jobs">
          <svg class="h-3.5 w-3.5" aria-hidden="true">
            <use :href="iconHref('clock')" />
          </svg>
          <span class="font-medium text-[var(--gl-text-color-default)]">{{ project.jobCount }}</span>
          {{ project.jobCount === 1 ? "job" : "jobs" }}
        </span>

        <!-- Open MRs -->
        <span class="flex items-center gap-1 text-xs text-[var(--gl-text-color-subtle)]" title="Open merge requests">
          <svg class="h-3.5 w-3.5" aria-hidden="true">
            <use :href="iconHref('merge-request')" />
          </svg>
          <span class="font-medium text-[var(--gl-text-color-default)]">{{ project.totalOpenMrs }}</span>
          {{ project.totalOpenMrs === 1 ? "MR" : "MRs" }}
        </span>
      </div>

      <!-- Admin actions -->
      <div class="flex items-center gap-3 border-l border-[var(--gl-border-color-default)] pl-4">
        <div class="flex">
          <button
            :id="`sync-btn-${project.id}`"
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-l-[var(--gl-action-border-radius)] border border-[var(--gl-button-confirm-secondary-border-color-default)] text-[var(--gl-button-confirm-secondary-foreground-color-default)] hover:bg-[var(--gl-button-confirm-secondary-background-color-hover)] hover:text-[var(--gl-button-confirm-secondary-foreground-color-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)] disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!isAdmin"
            @click="emit('sync-request', { projectId: project.id })"
          >
            <GlIcon name="retry" :size="14" />
          </button>
          <GlPopover :target="`sync-btn-${project.id}`" placement="top">
            Trigger project configuration and merge request state sync job
          </GlPopover>
          <button
            :id="`delete-btn-${project.id}`"
            type="button"
            :disabled="!isAdmin"
            class="flex h-7 w-7 items-center justify-center rounded-r-[var(--gl-action-border-radius)] border border-l-0 border-[var(--gl-button-danger-primary-border-color-default)] bg-[var(--gl-button-danger-primary-background-color-default)] text-[var(--gl-button-danger-primary-foreground-color-default)] hover:bg-[var(--gl-button-danger-primary-background-color-hover)] hover:border-[var(--gl-button-danger-primary-border-color-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)] disabled:cursor-not-allowed disabled:opacity-50"
            @click="
              emit('delete-request', {
                projectId: project.id,
                projectName: project.name
              })
            "
          >
            <GlIcon name="remove" :size="14" />
          </button>
          <GlPopover :target="`delete-btn-${project.id}`" placement="top"> Delete project </GlPopover>
        </div>
        <span class="border-l border-[var(--gl-border-color-default)] self-stretch"></span>
        <div class="leading-[0]" title="Enable/disable dependency updates">
          <GlToggle
            :model-value="project.enabled"
            :disabled="!isAdmin"
            label="Enabled"
            label-position="hidden"
            @update:model-value="emit('toggle', project, $event)"
          />
        </div>
      </div>
    </div>
  </li>
</template>
